// Панель раздела над картой: шапка-ручка, тело с прокруткой, подвал.
//
// Одна на все разделы со своей картой, чтобы панель везде вела себя одинаково:
// тащится за значок и пустое поле шапки, а заголовок и счётчик выделяются
// мышью (`data-panel-text`) — их копируют в переписку. На телефоне панель
// стоит на месте: тащить её пальцем значило бы спорить с картой.

import type { ReactNode } from 'react'
import { useT } from '../i18n'
import { PANEL_TEXT_ATTR, useFloatingPanel } from './floatingPanel'
import { useWide } from './useWide'

/** Отступ домашнего места панели от угла карты (`left-3 top-3`). */
export const MAP_PANEL_HOME = { x: 12, y: 12 }

const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar'
const text = { [PANEL_TEXT_ATTR]: '' }

export function Grip({ className = 'text-faint' }: { className?: string }) {
  return (
    <svg aria-hidden viewBox="0 0 8 14" className={`h-3.5 w-2 shrink-0 ${className}`}>
      {[2, 7, 12].flatMap(y => [1.5, 6.5].map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.2" fill="currentColor" />))}
    </svg>
  )
}

export function MapPanel({
  storageKey, title, meta, actions, children, footer,
  className = 'w-64', surface = 'bg-abyss/95 backdrop-blur',
}: {
  /** Под этим ключом браузер помнит, куда панель отвели. */
  storageKey: string
  /** Заголовок раздела; выделяется мышью. Без него шапка — одна ручка. */
  title?: ReactNode
  /** Число рядом с заголовком; тоже выделяется. */
  meta?: ReactNode
  /** Кнопки в шапке справа. */
  actions?: ReactNode
  children: ReactNode
  footer?: ReactNode
  /** Ширина панели. */
  className?: string
  /** Фон: у «Карт» и «Моделей» панель непрозрачна. */
  surface?: string
}) {
  const t = useT()
  const wide = useWide()
  const panel = useFloatingPanel({ storageKey, home: MAP_PANEL_HOME, enabled: wide })
  const cursor = wide ? (panel.dragging ? 'cursor-grabbing' : 'cursor-grab') : ''

  return (
    <aside ref={el => { panel.panelRef.current = el }} style={panel.style}
      className={`pointer-events-auto absolute left-3 top-3 z-10 flex max-h-[calc(100%-24px)] max-w-[calc(100%-24px)] flex-col overflow-hidden rounded-xl border border-hull shadow-console ${surface} ${className} ${panel.dragging ? 'select-none' : ''}`}>
      <div {...panel.handleProps} tabIndex={wide ? 0 : undefined}
        aria-label={wide ? t('map.panel.move') : undefined} title={wide ? t('map.panel.move') : undefined}
        style={wide ? { touchAction: 'none' } : undefined}
        className={`flex shrink-0 items-center gap-2 px-3 ${title ? 'pb-2 pt-2.5' : 'justify-center py-1.5'} ${FOCUS} ${cursor}`}>
        {wide && <Grip />}
        {title && (
          <h1 {...text} className="min-w-0 cursor-text select-text font-heading text-[14px] font-semibold leading-snug text-foam">
            {title}
          </h1>
        )}
        {/* Пустое поле шапки — тоже ручка: за текст панель не тащат. */}
        {title && <span aria-hidden className="min-w-2 flex-1 self-stretch" />}
        {meta != null && (
          <span {...text} className="shrink-0 cursor-text select-text text-[12px] tabular-nums text-mist">{meta}</span>
        )}
        {actions}
      </div>
      <div className="console-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
      {footer && <div className="shrink-0 border-t border-hull px-3 py-2">{footer}</div>}
    </aside>
  )
}
